import { useQuery } from '@tanstack/react-query';
import {
  Select,
  SelectContent,
  SelectIcon,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { pcmpRequestCategoriesForSubmitQueryOptions } from '../../api/pcmpRequests.api';
import type { PcmpActionPlanKey } from '../../types/pcmpRequestCategory.types';

const ALL_CATEGORIES = '__all__';

interface PcmpRequestCategoryFilterProps {
  /** The category code, or undefined for every category. */
  value: string | undefined;
  onChange: (code: string | undefined) => void;
  /** Only the categories that run this action (Urgent Requests: Urgent Payment). */
  actionPlanKey?: PcmpActionPlanKey;
}

/**
 * Narrows a request list to one category (legacy LDS-768). Lists the
 * categories the requester can raise; left out when they cannot be read.
 */
export function PcmpRequestCategoryFilter({
  value,
  onChange,
  actionPlanKey,
}: PcmpRequestCategoryFilterProps) {
  const { data: categories, isError } = useQuery(
    pcmpRequestCategoriesForSubmitQueryOptions(undefined),
  );
  if (isError) return null;

  const items = [
    { value: ALL_CATEGORIES, label: 'All categories' },
    ...(categories ?? [])
      .filter((category) => !actionPlanKey || category.actionPlanKey === actionPlanKey)
      .map((category) => ({ value: category.code, label: category.name })),
  ];

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-muted-foreground">Category</span>
      <Select<string>
        items={items}
        value={value ?? ALL_CATEGORIES}
        onValueChange={(next) =>
          onChange(next === null || next === ALL_CATEGORIES ? undefined : next)
        }
      >
        <SelectTrigger aria-label="Request category" className="h-9 w-52">
          <SelectValue />
          <SelectIcon />
        </SelectTrigger>
        <SelectContent>
          {items.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
